import { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import LazyVideo from "../components/LazyVideo";
import { VIDEOS, POSTERS } from "../lib/media";

const CHECKS = [
  "Data sovereignty & residency mapping",
  "Access controls & identity hardening",
  "Supply-chain & SBOM verification",
  "AI usage & model-risk policy",
  "Incident response runbooks",
  "Continuous monitoring & evidence",
  "Personnel screening alignment",
  "AUKUS-aligned export controls",
];

interface Row { label: string; status: "Ready" | "Gaps" | "Plan"; tone: string; pct: number; }
const ROWS: Row[] = [
  { label: "Essential Eight maturity",     status: "Ready", tone: "text-emerald-300/90", pct: 92 },
  { label: "ISO 27001 readiness",          status: "Ready", tone: "text-emerald-300/90", pct: 88 },
  { label: "NIST CSF mapping",             status: "Gaps",  tone: "text-amber-300/90",   pct: 64 },
  { label: "AI risk register",             status: "Ready", tone: "text-emerald-300/90", pct: 81 },
  { label: "Vendor SBOM coverage",         status: "Gaps",  tone: "text-amber-300/90",   pct: 57 },
  { label: "Defence-ready evidence pack",  status: "Plan",  tone: "text-white/40",       pct: 30 },
];

interface ProgrammeTileProps {
  video?: string;
  poster?: string;
  imageSlot?: boolean;
  slotId?: string;
  placeholder?: string;
  label: string;
  title: string;
  desc: string;
  tint: string;
  chip: string;
  href: string;
  delay?: number;
}
function ProgrammeTile({ video, poster, imageSlot, slotId, placeholder, label, title, desc, tint, chip, href, delay = 0 }: ProgrammeTileProps) {
  return (
    <Reveal delay={delay}>
      <article className="liquid-glass rounded-3xl overflow-hidden h-full flex flex-col group relative min-h-[420px]">
        <div className="relative aspect-[4/3] overflow-hidden">
          {imageSlot ? (
            <div
              id={slotId}
              data-image-slot={slotId}
              className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 text-white/40 text-xs tracking-widest uppercase"
            >
              {placeholder || "Drop submarine image here"}
            </div>
          ) : video ? (
            <LazyVideo
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-110"
              src={video} poster={poster}
            />
          ) : null}
          <div className={`absolute inset-0 bg-gradient-to-b ${tint} mix-blend-multiply pointer-events-none`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40 pointer-events-none" />
          <div className="absolute top-4 left-4 liquid-glass rounded-full px-3 py-1 text-white text-[10px] tracking-widest uppercase z-10">{chip}</div>
          <a href={href} className="absolute top-4 right-4 liquid-glass rounded-full p-2 text-white z-10 hover:bg-white/5 transition-colors" aria-label={title}><ArrowUpRight size={16} /></a>
        </div>
        <div className="p-6 md:p-7 flex flex-col flex-1">
          <div className="text-white/40 text-[10px] tracking-widest uppercase">{label}</div>
          <h4 className="font-instrument text-white text-xl md:text-2xl tracking-tight mt-3 leading-[1.15]">{title}</h4>
          <p className="text-white/55 text-sm leading-relaxed mt-3">{desc}</p>
        </div>
      </article>
    </Reveal>
  );
}

export default function Defence() {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!panelRef.current) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.3 });
    io.observe(panelRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section id="defence" className="bg-black py-28 md:py-40 px-6 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(ellipse at center, rgba(255,255,255,0.03) 0%, transparent 55%)" }} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal><div className="text-white/40 text-sm tracking-widest uppercase">Defence-Ready</div></Reveal>
        <Reveal delay={100}>
          <h2 className="font-instrument text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mt-6 leading-[1.05]">
            AUKUS Ready Audits,<br /> policies and <em className="italic-serif text-white/50">evidence</em>
            <br className="hidden md:block" />, generated, not assembled.
          </h2>
        </Reveal>
        <Reveal delay={220}>
          <p className="mt-8 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
            A productised programme for organisations supplying defence, government and critical-infrastructure customers. Deep audit, AI-assisted policy generation, and an evidence pack you can hand to procurement on day one.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 mt-16">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CHECKS.map((c, i) => (
                <Reveal key={c} kind="reveal-soft" delay={i * 50}>
                  <div className="liquid-glass rounded-2xl px-4 py-3 flex items-start gap-3">
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-white/90 flex-none">
                      <Check size={12} />
                    </div>
                    <div className="text-white/80 text-sm leading-snug">{c}</div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal kind="reveal-soft" delay={400}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact" className="rounded-full px-8 py-3 text-black text-sm font-medium bg-white hover:bg-white/90 transition-colors inline-flex items-center gap-2">
                  Request a Defence-Ready Check <ArrowRight size={16} />
                </a>
                <a href="#cases" className="liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors">
                  See a sample report
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal kind="reveal-x-right" delay={150}>
            <div ref={panelRef} className="liquid-glass rounded-3xl p-6 md:p-8 h-full">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="text-white/40 text-xs tracking-widest uppercase">Live readiness</div>
                  <div className="font-instrument text-2xl text-white mt-1">Your Company Pty Ltd</div>
                </div>
                <div className="text-right">
                  <div className="text-white/40 text-xs tracking-widest uppercase">Score</div>
                  <div className="font-instrument text-4xl text-white mt-1">
                    {shown ? 78 : 0}<span className="text-white/30 text-2xl">/100</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {ROWS.map((r, i) => (
                  <div key={r.label} className="flex items-center justify-between gap-4 text-sm">
                    <div className="text-white/80 truncate">{r.label}</div>
                    <div className="flex items-center gap-3 flex-none">
                      <div className="w-24 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-white/90 rounded-full"
                             style={{ width: shown ? `${r.pct}%` : 0, transition: `width 1100ms cubic-bezier(.2,.7,.2,1) ${i * 70}ms` }} />
                      </div>
                      <span className={`text-xs tracking-widest uppercase ${r.tone} w-12 text-right`}>{r.status}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hairline mt-6 mb-4" />
              <div className="flex items-center justify-between">
                <div className="text-white/40 text-xs tracking-widest uppercase">Sample dashboard · not client data</div>
                <a
                  href="#contact"
                  className="text-white/80 hover:text-white text-sm"
                >
                  Order Audit Now →
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Programmes - dynamic imagery tiles */}
        <Reveal>
          <div className="flex items-end justify-between gap-6 flex-wrap mt-24 md:mt-32 mb-10">
            <div>
              <div className="text-white/40 text-sm tracking-widest uppercase">Programmes</div>
              <h3 className="font-instrument text-3xl md:text-5xl text-white tracking-tight mt-3 leading-[1.1]">
                Where defence-ready <em className="italic-serif text-white/50">meets</em> industry.
              </h3>
            </div>
            <div className="text-white/40 text-xs tracking-widest uppercase max-w-xs">AUKUS-aligned · Critical infrastructure · Resources</div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ProgrammeTile
            video={VIDEOS.submarine}
            poster={POSTERS.submarine}
            label="01 · Submarines & AUKUS"
            title="Sovereign capability for the AUKUS supply chain"
            desc="Defence-ready audits, AI policy generation and software for vendors entering the submarine and naval programmes. Built around export-control and personnel-screening realities."
            tint="from-cyan-500/35 via-blue-700/25 to-slate-950/80"
            chip="Cleared environments"
            href="/defence/aukus-supply-chain.html"
          />
          <ProgrammeTile
            video={VIDEOS.weldingPipes}
            poster={POSTERS.weldingPipes}
            label="02 · Subcontractors & Compliance"
            title="Defence-ready policies and audits for AUKUS subcontractors"
            desc="Systematic audits and AI-generated compliance packs for the subcontractors, fabricators and tradies entering the AUKUS and defence supply chain. Built around AS/NZS standards, export-control obligations and Essential Eight uplift."
            tint="from-orange-500/35 via-amber-700/25 to-slate-950/80"
            chip="Subcontractor ready"
            href="/defence/subcontractor-compliance.html"
            delay={120}
          />
          <ProgrammeTile
            video={VIDEOS.aiTransformationConsultancy}
            poster={POSTERS.aiTransformationConsultancy}
            label="03 · AI Integration & Productivity"
            title="AI integration for AUKUS subcontractors - productivity, security and transparency"
            desc="Embed AI into the daily workflows of subcontractors supplying defence and critical infrastructure. Boost productivity, surface compliance gaps in real time, and give primes full supply-chain transparency without the overhead."
            tint="from-indigo-500/30 via-blue-800/25 to-slate-950/80"
            chip="AI-enabled"
            href="/defence/ai-productivity.html"
            delay={240}
          />
        </div>
      </div>
    </section>
  );
}
